import { ArrowLeft, ArrowRight } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Heading } from '@unistocks/components/text/text';
import {
  ListRenderItem,
  VirtualList,
  VirtualListRef
} from '@unistocks/components/virtual-list/list';
import { getValueCells } from '@unistocks/features/dashboard/components/calculated-value/value-cells';
import { EventViewItemProps } from '@unistocks/features/dashboard/components/dashboard-data/event-view/layouts/event-view-item-props';
import {
  DashboardItemFrame,
  DashboardItemProps
} from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item';
import { DashboardItemHeader } from '@unistocks/features/dashboard/components/dashboard-item/dashboard-item-header';
import { ValueListItem } from '@unistocks/features/dashboard/components/value-list-item/value-list-item';
import { CalculatedValueDto } from '@unistocks/proxy/axios-client';
import { useDashboardGetEventViewDataQuery } from '@unistocks/proxy/axios-client/DashboardQuery';
import { useRef } from 'react';
import { getVariableValue, isWeb, Spinner, View } from 'tamagui';

export const EventViewCarousel = ({
  item,
  itemLayout,
  openSettings,
  ...frameProps
}: DashboardItemProps) => {
  const { id, settings } = item;
  const listRef = useRef<VirtualListRef<CalculatedValueDto>>(null);
  const scrollIndex = useRef(0);

  const size2 = getVariableValue('$6', 'size');
  const space4 = getVariableValue('$4', 'space');
  const space2 = getVariableValue('$2', 'space');
  const space15 = getVariableValue('$1.5', 'space');

  const containerWidth = itemLayout?.width ?? 0;
  const itemSizeOffset = isWeb ? space4 : size2;
  const itemSize = containerWidth - itemSizeOffset;
  const itemSpacing = isWeb ? space15 : space2;
  const snapToOffset = itemSize + itemSpacing * 2;
  const carouselPadding = (containerWidth - itemSize - itemSpacing * 2) / 2;

  const title = settings.title?.trim();

  const onPreviousPress = async () => {
    const next = Math.max(0, scrollIndex.current - 1);
    await listRef?.current?.scrollToIndex({ index: next, animated: true });
    scrollIndex.current = next;
  };

  const onNextPress = async () => {
    const next = Math.min((settings.values?.length ?? 0) - 1, scrollIndex.current + 1);
    await listRef?.current?.scrollToIndex({ index: next, animated: true });
    scrollIndex.current = next;
  };

  const renderItem: ListRenderItem<CalculatedValueDto> = ({ item: calculatedValue, index }) => (
    <DashboardItemFrame
      {...frameProps}
      w={itemSize}
      mx={itemSpacing}
      $platform-native={{
        my: '$2'
      }}
    >
      <DashboardItemHeader
        item={item}
        openSettings={openSettings}
        pos={'absolute'}
        top={'$3'}
        right={'$3'}
        $platform-native={{
          top: '$3'
        }}
      />
      <EventViewCarouselItem dashboardItemId={id} value={calculatedValue} index={index} />
    </DashboardItemFrame>
  );

  return (
    <>
      {isWeb || title ? (
        <Row justifyContent="space-between">
          {title ? (
            <Heading className="drag-handle" ml={'$3'} ellipsis numberOfLines={1}>
              {title}
            </Heading>
          ) : null}
          {isWeb ? (
            <Row>
              <Pressable withFeedback withScale onPress={onPreviousPress}>
                <ArrowLeft strokeWidth={3} size={'$2'} color={'$headingFont'} />
              </Pressable>
              <Pressable withFeedback withScale onPress={onNextPress}>
                <ArrowRight strokeWidth={3} size={'$2'} color={'$headingFont'} />
              </Pressable>
            </Row>
          ) : null}
        </Row>
      ) : null}
      <VirtualList
        ref={listRef}
        horizontal
        keyExtractor={keyExtractor}
        data={settings.values}
        extraData={item}
        renderItem={renderItem}
        snapToAlignment="center"
        snapToOffsets={settings.values?.map((_, index) => index * snapToOffset)}
        decelerationRate={'fast'}
        showsHorizontalScrollIndicator={false}
        fadingEdgeLength={20}
        scrollEventThrottle={16}
        scrollEnabled={!isWeb}
        contentContainerStyle={{
          px: isWeb ? 0 : carouselPadding
        }}
      />
    </>
  );
};

const EventViewCarouselItem: React.FC<EventViewItemProps> = ({
  dashboardItemId,
  value,
  index,
  ...props
}) => {
  const {
    data: result,
    isSuccess,
    isPending,
    isStale,
    isError
  } = useDashboardGetEventViewDataQuery({
    id: dashboardItemId,
    index
  });
  const { cells } = getValueCells(result, { isStale });
  const cell = isSuccess ? cells[result.valueType] : null;

  return (
    <ValueListItem alignItems="center" justifyContent="space-between" {...props}>
      <ValueListItem.Image size={'$4'} calculatedValue={result} />

      <Column alignItems="center" flex={8}>
        <ValueListItem.Label
          className="drag-handle"
          size={'$7'}
          mr={'$4'}
          numberOfLines={1}
          $platform-web={{
            whiteSpace: 'nowrap',
            textWrap: 'nowrap',
            cursor: 'grab'
          }}
        >
          {result?.displayName ?? value.displayName}
        </ValueListItem.Label>
        <ValueListItem.Value size={'$8'} mr={'$4'} status={cell?.status} textAlign="center">
          {isSuccess ? cell?.formattedValue : isError ? '-' : null}
          {isPending ? <Spinner color={'$bodyFont'} /> : null}
        </ValueListItem.Value>
      </Column>

      <View flex={0.5} />
    </ValueListItem>
  );
};

EventViewCarouselItem.displayName = 'EventViewCarouselItem';

const keyExtractor = (item: any, index: number) => index.toString();
